import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { logoutUri } from '@/lib/kinde';
import './SignOutButton.css';

/**
 * Sign out.
 *
 * Calls Kinde's logout endpoint rather than just dropping the local token, and the difference
 * matters: clearing our copy would leave the Kinde session alive, so the next sign-in would
 * complete silently without ever asking who they are. On a shared or borrowed device that
 * looks exactly like "sign out didn't work" — because it didn't.
 *
 * `logoutUri` must be one of the application's registered logout redirect URLs. It is derived
 * from `window.location.origin`, so every origin needs registering; an unregistered one fails
 * at Kinde with a generic error after the session has already been destroyed, stranding the
 * user on Kinde's domain.
 *
 * Deliberately not a confirm dialog. Signing out is cheap to undo and asking twice is friction
 * on the one action a person takes when they want to be certain they are out.
 */
export function SignOutButton({ variant = 'row' }: { variant?: 'row' | 'button' }) {
  const { logout } = useKindeAuth();

  return (
    <button
      type="button"
      className={variant === 'row' ? 'signout signout--row' : 'signout signout--button'}
      // `allSessions` is left at its default of false on purpose. This application is shared
      // with the current product, so signing out of Insights with allSessions:true would sign
      // the same person out of the other product too — a side effect nobody asked for from a
      // button in this app.
      onClick={() => void logout({ redirectUrl: logoutUri() })}
    >
      Sign out
    </button>
  );
}
